import { Building2, DollarSign, ChevronLeft, SearchX } from 'lucide-react';
import { Badge, EmptyState } from '@elka/ui';
import { DEAL_STAGE_LABELS, DEAL_QUOTING_STATUS_LABELS } from '@elka/types';
import { formatNumberWithCommas } from '@elka/utils';
import type { DealWithQuotingStatus } from '../hooks/useDealsWithQuotingStatus';
import { STAGE_BADGE_VARIANT, QUOTING_STATUS_BADGE_VARIANT } from '../lib/dealStatusColors';

interface DealsCardViewProps {
  deals: DealWithQuotingStatus[];
  onDealClick?: (dealId: string) => void;
}

export function DealsCardView({ deals, onDealClick }: DealsCardViewProps) {
  if (deals.length === 0) {
    return (
      <EmptyState dense icon={<SearchX size={28} />} title="אין עסקאות התואמות לסינון שנבחר" />
    );
  }

  return (
    <div className="space-y-2.5 p-4">
      {deals.map((deal, i) => (
        <div
          key={deal.id}
          onClick={() => onDealClick?.(deal.id)}
          className={`deal-card deal-card-${deal.quotingStatus} p-4`}
          style={{ animationDelay: `${i * 0.05}s` }}
        >
          <div className="mb-2.5 flex items-start justify-between">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <h3 className="truncate text-base font-bold text-balance text-gray-900">
                {deal.name}
              </h3>
            </div>
            <div className="mr-2 flex flex-shrink-0 items-center gap-1.5">
              <Badge variant={STAGE_BADGE_VARIANT[deal.stage]}>
                {DEAL_STAGE_LABELS[deal.stage]}
              </Badge>
            </div>
          </div>

          <div className="mb-2.5 flex items-center gap-2 text-sm text-gray-500">
            <Building2 size={13} className="flex-shrink-0 text-gray-400" />
            <span className="truncate">{deal.companyName}</span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant={QUOTING_STATUS_BADGE_VARIANT[deal.quotingStatus]}>
                {DEAL_QUOTING_STATUS_LABELS[deal.quotingStatus]}
              </Badge>
            </div>

            <div className="flex items-center gap-2">
              {deal.value != null && deal.value > 0 && (
                <div className="flex items-center gap-1 rounded-lg bg-gray-50 px-2.5 py-1">
                  <DollarSign size={11} className="text-gray-400" />
                  <span className="text-xs font-semibold text-gray-600 tabular-nums">
                    {`₪${formatNumberWithCommas(String(Math.round(deal.value)))}`}
                  </span>
                </div>
              )}
              {deal.quoteTotal != null && deal.quoteTotal > 0 && (
                <div className="flex items-center gap-1 rounded-lg bg-blue-50 px-2.5 py-1">
                  <span className="text-xs font-bold text-blue-700 tabular-nums">
                    {`₪${formatNumberWithCommas(String(Math.round(deal.quoteTotal)))}`}
                  </span>
                </div>
              )}
              <ChevronLeft size={14} className="flex-shrink-0 text-gray-300" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
